import { useRef, useState, useEffect } from 'react'

const CHOICE_LABELS = { yes: 'نعم', no: 'لا', na: 'لا ينطبق' }

// دالة مساعدة لتوحيد خصائص الإدخال
const useInputHandlers = (onChange) => ({
    onClick: (e) => e.currentTarget.focus(),
    onChange: (e) => onChange(e.target.value),
    onKeyDown: (e) => {
        if (e.key === 'Enter' && e.currentTarget.tagName === 'INPUT') {
            e.preventDefault()
            e.currentTarget.blur()
        }
    }
})

function TextField({ value, onChange, fontSize, isTextArea }) {
    const handlers = useInputHandlers(onChange)
    const commonStyles = {
        width: '100%', height: '100%', margin: 0, padding: '0 4px',
        pointerEvents: 'auto', border: 0, textAlign: 'right',
        fontSize, color: '#333', backgroundColor: 'transparent',
    }

    return isTextArea ? (
        <textarea {...handlers} dir='rtl' value={value || ''} style={{ ...commonStyles, resize: 'none', lineHeight: 1.3, fontFamily: 'inherit' }} />
    ) : (
        <input {...handlers} type='text' dir='rtl' value={value || ''} style={commonStyles} />
    )
}

function ChoiceField({ value, onChange, options, fontSize }) {
    return (
        <div style={{ display: 'flex', width: '100%', height: '100%', pointerEvents: 'auto', gap: 2 }}>
            {options.map((opt) => {
                const selected = value === opt
                return (
                    <div
                        key={opt}
                        onClick={() => onChange(selected ? null : opt)}
                        style={{
                            flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                            cursor: 'pointer', userSelect: 'none', borderRadius: 4, fontSize,
                            fontWeight: selected ? 700 : 400,
                            color: selected ? '#1565c0' : '#444',
                            backgroundColor: selected ? 'rgba(21,101,192,0.14)' : 'transparent',
                            border: `1px solid ${selected ? '#1565c0' : 'transparent'}`,
                        }}
                    >
                        {CHOICE_LABELS[opt]}
                    </div>
                )
            })}
        </div>
    )
}

function ImageField({ value, onChange, readonly }) {
    const inputRef = useRef(null)
    const [preview, setPreview] = useState(null)

    useEffect(() => {
        if (typeof value === 'string') {
            setPreview(value)
        } else if (value instanceof File) {
            const objectUrl = URL.createObjectURL(value)
            setPreview(objectUrl)
            return () => URL.revokeObjectURL(objectUrl)
        } else {
            setPreview(null)
        }
    }, [value])

    const handleFile = (e) => {
        const file = e.target.files?.[0]
        if (file) onChange(file)
    }

    return (
        <div
            onClick={() => !readonly && inputRef.current?.click()}
            style={{
                width: '100%', height: '100%', overflow: 'hidden', borderRadius: 4,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                pointerEvents: 'auto', cursor: readonly ? 'default' : 'pointer',
                border: (preview || readonly) ? '1px solid transparent' : '1px dashed #999',
                backgroundColor: (preview || readonly) ? 'transparent' : 'rgba(0,0,0,0.03)',
            }}
        >
            {!readonly && (
                <input
                    ref={inputRef}
                    type='file'
                    accept='image/*'
                    capture='environment'
                    onChange={handleFile}
                    style={{ display: 'none' }}
                />
            )}
            {preview 
                ? (
                    <img
                        src={preview}
                        alt=''
                        style={{
                            maxWidth: '100%',
                            maxHeight: '100%',
                            objectFit: 'contain'
                        }}
                    />
                )
                : (!readonly && <span style={{ fontSize: 11, color: '#777' }}>+ إضافة صورة</span>)
            }
        </div>
    )
}

export default function Field({ type, value, onChange, fontSize, readonly }) {
    switch (type) {
        case 'ta': return <TextField value={value} onChange={onChange} fontSize={fontSize} isTextArea />
        case 'yn': return <ChoiceField value={value} onChange={onChange} options={['yes', 'no']} fontSize={fontSize} />
        case 'yna': return <ChoiceField value={value} onChange={onChange} options={['yes', 'no', 'na']} fontSize={fontSize} />
        case 'i': return <ImageField value={value} onChange={onChange} readonly={readonly} />
        case 't': default: return <TextField value={value} onChange={onChange} fontSize={fontSize} />
    }
}